import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type NodeTypes,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, Empty, Spin, Typography, Button, Space } from 'antd';
import { ExpandAltOutlined, ShrinkOutlined } from '@ant-design/icons';
import { JobNode } from './JobNode';
import { StepNode } from './StepNode';
import {
  buildHierarchicalGraph,
  buildVisibleGraph,
  getNodeStatusColor,
  type JobEvent,
  type HierarchicalGraphData,
} from '../../utils/graphLayout';

const { Text } = Typography;

const nodeTypes: NodeTypes = {
  jobNode: JobNode,
  stepNode: StepNode,
};

interface JobGraphProps {
  events: JobEvent[];
  loading?: boolean;
  onNodeClick?: (nodeId: string, data: unknown) => void;
  height?: number | string;
}

export function JobGraph({
  events,
  loading = false,
  onNodeClick,
  height = 500,
}: JobGraphProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Build hierarchical structure once
  const hierarchicalData = useMemo<HierarchicalGraphData>(
    () => buildHierarchicalGraph(events),
    [events]
  );

  // Build visible graph based on expanded state
  const graphData = useMemo(
    () => buildVisibleGraph(hierarchicalData, expandedNodes),
    [hierarchicalData, expandedNodes]
  );

  const nodesWithEdges = useMemo(
    () => ({
      nodes: graphData.nodes,
      edges: graphData.edges.map((edge) => ({
        ...edge,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 15,
          height: 15,
        },
        style: {
          strokeWidth: 2,
        },
      })),
    }),
    [graphData]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(nodesWithEdges.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(nodesWithEdges.edges);

  // Update nodes/edges when graph data changes
  useEffect(() => {
    setNodes(nodesWithEdges.nodes);
    setEdges(nodesWithEdges.edges);
  }, [nodesWithEdges, setNodes, setEdges]);

  const totalNodes = hierarchicalData.allNodes.size;
  const isExpanded = expandedNodes.size > 0;
  // Show expand/collapse buttons if there are more than just job_start and job_end
  const hasExpandableNodes = totalNodes > 2;

  const expandAll = useCallback(() => {
    // Set any value to indicate expanded state
    setExpandedNodes(new Set(['expanded']));
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      // Toggle expand/collapse when clicking on job_start node with hidden children
      if (node.id === 'job_start' && node.data?.hasChildren && !isExpanded) {
        expandAll();
        return;
      }

      if (onNodeClick) {
        onNodeClick(node.id, node.data);
      }
    },
    [onNodeClick, isExpanded, expandAll]
  );

  if (loading) {
    return (
      <Card style={{ height }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 300,
          }}
        >
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!events.length) {
    return (
      <Card style={{ height }}>
        <Empty
          description="No events to display"
          style={{ marginTop: 100 }}
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text>
            Job Execution Graph ({nodes.length}/{totalNodes} nodes visible)
          </Text>
          {hasExpandableNodes && (
            <Space size="small">
              {!isExpanded ? (
                <Button
                  size="small"
                  icon={<ExpandAltOutlined />}
                  onClick={expandAll}
                >
                  Show Steps
                </Button>
              ) : (
                <Button
                  size="small"
                  icon={<ShrinkOutlined />}
                  onClick={collapseAll}
                >
                  Hide Steps
                </Button>
              )}
            </Space>
          )}
        </Space>
      }
      style={{ height }}
      styles={{ body: { height: 'calc(100% - 57px)', padding: 0 } }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        defaultEdgeOptions={{
          animated: false,
        }}
      >
        <Background />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            const status = node.data?.status as string;
            return getNodeStatusColor(status);
          }}
          maskColor="rgba(0, 0, 0, 0.1)"
          style={{ height: 100 }}
        />
      </ReactFlow>
    </Card>
  );
}
